import { type FunctionComponent } from 'react';

import { Image } from '@/core/Image/Image';
import { isB2B } from '@/core/settings/utils';
import { BannerCampaign } from '@/productDiscovery/CommercialAnimation/interfaces/bannerCampaign';
import fetchedImgSrc from '@/sellerAdsManagement/PartnerAdBannerCampaign/assets/b2b-background.png';
import {
  defaultThemeColors,
  ThemeColors,
} from '@/sellerAdsManagement/PartnerAdBannerCampaign/interfaces/themeColors';
import { usePartnerAdTheme } from '@/sellerAdsManagement/PartnerAdTheme/hooks/usePartnerAdTheme';

export interface BrandBlockProps {
  banner: BannerCampaign;
}

const SIZES = {
  small: {
    ldpi: {
      width: 158,
      height: 100,
    },
    hdpi: {
      width: 316,
      height: 200,
    },
  },
  medium: {
    ldpi: {
      width: 158,
      height: 100,
    },
    hdpi: {
      width: 316,
      height: 200,
    },
  },
  large: {
    ldpi: {
      width: 225,
      height: 142,
    },
    hdpi: {
      width: 450,
      height: 284,
    },
  },
};

export const BrandBlock: FunctionComponent<BrandBlockProps> = ({ banner }) => {
  const theme = usePartnerAdTheme();

  const themeColors = {
    '--banner-main-color':
      theme && theme.colors ? theme.colors.main : defaultThemeColors.main,
    '--banner-accent-color':
      theme && theme.colors ? theme.colors.accent : defaultThemeColors.accent,
    '--banner-labor_text-color':
      theme && theme.colors
        ? theme.colors.labor_text
        : defaultThemeColors.labor_text,
    '--banner-nut-color':
      theme && theme.colors ? theme.colors.nut : defaultThemeColors.nut,
  };

  const b2bStyle = isB2B() ? { backgroundImage: `url(${fetchedImgSrc})` } : '';

  return (
    <div
      className="absolute inset-0 flex rounded-container"
      style={themeColors as ThemeColors}
    >
      <div
        className="z-[1] flex h-full w-full rounded-container
          bg-[--banner-main-color] px-s py-m bg-blend-multiply tablet:px-m
          tablet:py-[20px] desktop:w-[70%]"
        style={{ ...b2bStyle }}
      >
        {banner.logoUrl && (
          <div
            className="mt-auto flex h-full w-[40%] items-center justify-center
              rounded-container bg-foundation-inverted p-m text-center
              desktop:p-l"
          >
            <Image
              className="h-auto max-h-full w-auto max-w-full object-contain"
              src={banner.logoUrl}
              alt="brand-logo"
              height={undefined}
              width={undefined}
            />
          </div>
        )}
        <div className="flex h-full w-[60%] flex-col justify-center pb-xs pl-m">
          <span
            className="overflow-hidden text-ellipsis whitespace-nowrap
              text-[15px] font-bold text-[--banner-accent-color]
              tablet:text-[calc(-0.80107px+100vw*0.026199)]
              desktop:text-[calc(-1.035294px+100vw*0.016176)] wide:text-[22px]"
          >
            {banner.title || ''}
          </span>
          <span
            className="text-[12px] text-[--banner-labor_text-color]
              tablet:text-[calc(-0.153664px+100vw*0.019701)]
              desktop:text-[calc(-0.8px+100vw*0.0125)] wide:text-[17px]"
          >
            {banner.subtitle || ''}
          </span>
        </div>
      </div>
      {banner.image && (
        <Image
          width={SIZES.small.ldpi.width}
          height={SIZES.small.ldpi.height}
          loading="eager"
          alt={banner.image.altText}
          src={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover`}
          srcSet={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover 1x, ${banner.image.mobileUrl}?w=${SIZES.small.hdpi.width}&h=${SIZES.small.hdpi.height}&fit=cover 2x`}
          className="absolute right-0 top-0 hidden h-full w-[32.5%]
            rounded-r-container bg-[--banner-nut-color] object-cover
            desktop:block"
        >
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.large.ldpi.width}&h=${SIZES.large.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.desktopUrl}?w=${SIZES.large.hdpi.width}&h=${SIZES.large.hdpi.height}&fit=cover&format=webp 2x`}
            media="(min-width: 1024px)"
            type="image/webp"
          />
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.large.ldpi.width}&h=${SIZES.large.ldpi.height}&fit=cover 1x, ${banner.image.desktopUrl}?w=${SIZES.large.hdpi.width}&h=${SIZES.large.hdpi.height}&fit=cover 2x`}
            media="(min-width: 1024px)"
          />
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.medium.ldpi.width}&h=${SIZES.medium.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.desktopUrl}?w=${SIZES.medium.hdpi.width}&h=${SIZES.medium.hdpi.height}&fit=cover&format=webp 2x`}
            media="(min-width: 536px)"
            type="image/webp"
          />
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.medium.ldpi.width}&h=${SIZES.medium.ldpi.height}&fit=cover 1x, ${banner.image.desktopUrl}?w=${SIZES.medium.hdpi.width}&h=${SIZES.medium.hdpi.height}&fit=cover 2x`}
            media="(min-width: 536px)"
          />
          <source
            srcSet={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.mobileUrl}?w=${SIZES.small.hdpi.width}&h=${SIZES.small.hdpi.height}&fit=cover&format=webp 2x`}
            type="image/webp"
          />
        </Image>
      )}
    </div>
  );
};

BrandBlock.displayName = 'BrandBlock';
